// frontend/src/hooks/useCalendarEvents.ts
// Brighella v2 (ADR-013 Merge A foundation) — Calendar Hub data hook.
//
// Single React Query consumer of /api/calendar/events. Used by:
//   - CalendarHub (the consolidated /calendar surface, all 6 view modes)
//   - any production / person workspace tab that needs a scoped calendar
//   - the future Outlook/Google sync layer (ADR-014 D13) reads the same
//     normalized shape.
//
// Stable cache key shape: range + alpha-sorted filter keys + alpha-sorted
// value arrays. Cached for 30s by default (calendar events don't change
// faster than that in real workflows).
//
// Returns the array of CalendarEvent rows in starts_at order, plus the
// total count and the resolved range.

import { useQuery, keepPreviousData, type UseQueryResult, type QueryClient } from '@tanstack/react-query'
import api from '../utils/api'

/**
 * Invalidate the Calendar Hub aggregate cache (`/calendar/events`).
 *
 * That endpoint UNIONs every calendar source — rehearsals, performances, events,
 * absences (PTO), holds (space bookings), tour stops, external productions and
 * holidays — into a single normalized stream. None of those source mutations
 * shares a cache key with `['calendar-events', ...]`, so unless each one calls
 * this in its `onSuccess`, the acting user sees a stale `/calendar` for up to the
 * 30s `staleTime` (or until a server WebSocket push happens to arrive, which is
 * not guaranteed for the user's own write). Reactivity audit 2026-06-06, finding #5.
 *
 * React Query matches by key prefix, so the partial `['calendar-events']` key
 * invalidates every cached range/filter/search combination at once.
 */
export function invalidateCalendar(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['calendar-events'] })
}

export type CalendarEventSubjectType =
  | 'rehearsal'
  | 'performance'
  | 'absence'
  | 'hold'
  | 'tour_stop'
  | 'external'
  | 'event'
  | 'holiday'

export type CalendarEventStatus = 'draft' | 'planned' | 'hold' | 'confirmed' | 'cancelled'

// productions.status — the LIVE production lifecycle enum (migration 002).
// Distinct from CalendarEventStatus: the clamped calendar_events.status
// collapses 'active' and 'completed' into 'confirmed', so the production
// status facet filters on this enum via a backend join instead.
export type CalendarProductionStatus =
  | 'planned'
  | 'active'
  | 'completed'
  | 'cancelled'

export interface CalendarEvent {
  id: number
  subject_type: CalendarEventSubjectType
  subject_id: number
  category: string
  title: string
  starts_at: string
  ends_at: string
  space_id: number | null
  production_id: number | null
  person_id: number | null
  status: CalendarEventStatus
  color_token: string | null
  source_table: string
  source_id: number
  payload: Record<string, unknown>
  created_at: string
  updated_at: string
  // Precomputed at hook-normalize time so per-render Date construction +
  // Intl.DateTimeFormat calls (the most expensive frontend cost per event)
  // run ONCE per fetch instead of once per render per chip. Views that need
  // them can read these directly; views that don't are unaffected.
  __startDate?: Date
  __endDate?: Date
  __startMs?: number
  __endMs?: number
  __startTimeLabel?: string
  __endTimeLabel?: string
}

export interface CalendarFilters {
  spaces?: number[]
  productions?: number[]
  people?: number[]
  subjectTypes?: CalendarEventSubjectType[]
  categories?: string[]
  statuses?: CalendarEventStatus[]
  productionStatuses?: CalendarProductionStatus[]
}

export interface UseCalendarEventsArgs {
  /** ISO date or datetime; the inclusive lower bound. */
  from: string
  /** ISO date or datetime; the exclusive upper bound. */
  to: string
  filters?: CalendarFilters
  /** Free-text search; sent to the backend as the `q` query param. */
  search?: string
  /** Disable the query without unmounting the consumer. */
  enabled?: boolean
}

export interface CalendarEventsResponse {
  events: CalendarEvent[]
  range: { from: string; to: string }
  count: number
}

// Shared Intl formatter — instantiated once, reused per event. Building a
// fresh Intl.DateTimeFormat per call costs ~50–200µs; reusing one drops the
// per-event cost to a single .format() call. Locale = `undefined` so the
// browser-default locale is honored, matching existing chip rendering.
const TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
})

/**
 * Add precomputed Date / millis / time-label fields to each event.
 * Pure: returns a new array of new objects, never mutates the input.
 *
 * The derived fields are flagged with a double-underscore prefix so they
 * are visually distinct from server-sent columns and grep-friendly.
 */
function normalizeEvents(events: CalendarEvent[] | undefined): CalendarEvent[] {
  if (!events || events.length === 0) return events ?? []
  const out: CalendarEvent[] = new Array(events.length)
  for (let i = 0; i < events.length; i++) {
    const e = events[i]
    const startDate = new Date(e.starts_at)
    const endDate = new Date(e.ends_at)
    const startMs = startDate.getTime()
    const endMs = endDate.getTime()
    out[i] = {
      ...e,
      __startDate: startDate,
      __endDate: endDate,
      __startMs: startMs,
      __endMs: endMs,
      __startTimeLabel: Number.isNaN(startMs) ? '' : TIME_FORMATTER.format(startDate),
      __endTimeLabel: Number.isNaN(endMs) ? '' : TIME_FORMATTER.format(endDate),
    }
  }
  return out
}

/** Stable cache-key shape: alpha-sort filter keys + sort each array. */
function stabilizeFilters(filters: CalendarFilters | undefined) {
  if (!filters) return {}
  const keys = Object.keys(filters).sort() as (keyof CalendarFilters)[]
  const out: Record<string, unknown> = {}
  for (const k of keys) {
    const v = filters[k]
    if (!v || v.length === 0) continue
    out[k] = [...v].sort()
  }
  return out
}

/** Convert filters to URL query params (csv) the backend route accepts. */
function filtersToParams(filters: CalendarFilters | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  if (!filters) return out
  if (filters.spaces?.length)       out.spaces       = filters.spaces.join(',')
  if (filters.productions?.length)  out.productions  = filters.productions.join(',')
  if (filters.people?.length)       out.people       = filters.people.join(',')
  if (filters.subjectTypes?.length) out.subjectTypes = filters.subjectTypes.join(',')
  if (filters.categories?.length)   out.categories   = filters.categories.join(',')
  if (filters.statuses?.length)     out.statuses     = filters.statuses.join(',')
  if (filters.productionStatuses?.length) {
    out.productionStatuses = filters.productionStatuses.join(',')
  }
  return out
}

export function useCalendarEvents({
  from,
  to,
  filters,
  search,
  enabled = true,
}: UseCalendarEventsArgs): UseQueryResult<CalendarEventsResponse> {
  const stableFilters = stabilizeFilters(filters)
  // Normalise so an empty / whitespace-only search never widens the cache key.
  const q = (search ?? '').trim()
  return useQuery({
    queryKey: ['calendar-events', from, to, stableFilters, q],
    enabled: enabled && Boolean(from) && Boolean(to),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: () =>
      api
        .get('/calendar/events', {
          params: { from, to, ...filtersToParams(filters), ...(q ? { q } : {}) },
        })
        .then((r) => {
          const data = r.data as CalendarEventsResponse
          return {
            ...data,
            events: normalizeEvents(data.events),
          }
        }),
  })
}

// Exposed for tests — real functions, not re-implementations.
export const _internals = { normalizeEvents, stabilizeFilters }
